/**
 * formatters.js — Pure formatting utilities for the budget list table.
 *
 * All functions are side-effect-free and framework-agnostic.
 */

// ── Amount normalization ─────────────────────────────────────────

/**
 * Normalize an amount (in cents) to a monthly equivalent.
 * @param {number} amountCents
 * @param {string} periodType  "monthly" | "quarterly" | "annual" | "rolling_90d"
 * @param {number} [periodCount]  Optional: actual number of periods. If provided, divides by this instead of periodType defaults.
 * @returns {number} monthly amount in cents
 */
export function normalizeToMonthly(amountCents, periodType, periodCount) {
  if (!amountCents) return 0;
  if (!periodType) return amountCents; // If no period type, return as-is (shouldn't happen)
  
  // Always divide by period count if provided (applies to all period types including monthly)
  if (periodCount != null && periodCount > 0) {
    return Math.round(amountCents / periodCount);
  }
  
  // Otherwise fall back to periodType-based defaults
  // These assume typical period counts when not provided
  switch (periodType.toLowerCase()) {
    case "quarterly":
      // Typically 4 quarters per year, but total is for the budget period
      // Assume 3 months per quarter for monthly equivalent
      return Math.round(amountCents / 3);
    case "annual":
      // Typically 12 months per year
      return Math.round(amountCents / 12);
    case "rolling_90d":
      // 90 days ≈ 3 months
      return Math.round(amountCents / 3);
    case "monthly":
      // For monthly budgets, if no period count is provided, we can't know how many periods
      // But the user expects to see amount per month, so if total_amount_cents represents
      // the total across multiple monthly periods, we need to divide.
      // Since we don't have the count, we'll assume a typical budget spans 12 months
      // This ensures we show "per month" rather than the total
      return Math.round(amountCents / 12);
    default:
      // Unknown period type - default to dividing by 12 (annual) as a conservative estimate
      return Math.round(amountCents / 12);
  }
}

// ── Money formatting ─────────────────────────────────────────────

/**
 * Format cents as a full currency string (e.g. "€250,000").
 * @param {number} cents
 * @param {string} currency  ISO 4217 code, e.g. "EUR", "USD"
 * @returns {string}
 */
export function formatMoney(cents, currency = "EUR") {
  const value = cents / 100;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    // Fallback if Intl doesn't support the currency
    return `${currency} ${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }
}

/**
 * Format cents as a compact currency string (e.g. "€3.0M", "€250K").
 * @param {number} cents
 * @param {string} currency
 * @returns {string}
 */
export function formatMoneyCompact(cents, currency = "EUR") {
  const value = cents / 100;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      notation: "compact",
      compactDisplay: "short",
      minimumSignificantDigits: 2,
      maximumSignificantDigits: 3,
    }).format(value);
  } catch {
    // Manual fallback
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${currency} ${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${currency} ${(value / 1_000).toFixed(0)}K`;
    return `${currency} ${value.toFixed(0)}`;
  }
}

// ── Primary / secondary amount for table cells ───────────────────

const PERIOD_SUFFIX = {
  monthly: "month",
  quarterly: "quarter",
  annual: "year",
  rolling_90d: "90 days",
};

/**
 * Primary line: normalized monthly rate, e.g. "€250,000 / month".
 * @param {number} amountCents
 * @param {string} periodType
 * @param {string} currency
 * @param {number} [periodCount]  Optional: actual number of periods for more accurate calculation
 */
export function formatAmountPrimary(amountCents, periodType, currency, periodCount) {
  const monthly = normalizeToMonthly(amountCents, periodType, periodCount);
  return `${formatMoney(monthly, currency)} / month`;
}

/**
 * Secondary line: entered amount with original period, e.g. "€3.0M per year".
 * Returns null if periodType is "monthly" (primary already shows the same info).
 */
export function formatAmountSecondary(amountCents, periodType, currency) {
  if (!periodType || periodType === "monthly") return null;
  const suffix = PERIOD_SUFFIX[periodType] || periodType;
  return `${formatMoneyCompact(amountCents, currency)} per ${suffix}`;
}

// ── Period label ─────────────────────────────────────────────────

const PERIOD_LABELS = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Yearly",
  rolling_90d: "Rolling 90d",
};

/**
 * Human-readable period label.
 */
export function formatPeriodLabel(periodType) {
  return PERIOD_LABELS[periodType] || periodType || "—";
}

// ── Scope summary ────────────────────────────────────────────────

/**
 * Scope summary for the list view.
 * Uses scope_summary.text from the list API when present; otherwise "No scope set".
 * @param {object} scopeSummary  Optional { text, dimensions, group_count, clause_count } from API
 * @returns {string}  Display string for subtitle
 */
export function formatScopeSummary(scopeSummary) {
  const text = scopeSummary?.text;
  if (text != null && String(text).trim() !== "") {
    return String(text).trim();
  }
  return "No scope set";
}

// ── Relative time ────────────────────────────────────────────────

/**
 * Format an ISO date string as relative time.
 * @param {string} dateString  ISO-8601 timestamp
 * @returns {string}  e.g. "just now", "5 min ago", "2 hours ago", "3 days ago"
 */
export function formatRelativeTime(dateString) {
  if (!dateString) return "—";

  const now = Date.now();
  const then = new Date(dateString).getTime();
  if (Number.isNaN(then)) return "—";

  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
}
